import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';

const TIMEFRAMES = ['M1', 'M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1', 'MN1'];

const EA_BADGE = {
  ready:          'badge-success',
  compiling:      'badge-warning',
  compile_failed: 'badge-danger',
};

const DEPLOYMENT_BADGE = {
  deployed: 'badge-success',
  stopped:  'badge-secondary',
  error:    'badge-danger',
};

function Modal({ title, onClose, children, size = '' }) {
  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,0.7)', overflowY: 'auto' }}
    >
      <div className={`modal-dialog ${size}`} style={{ marginTop: 16, marginBottom: 16 }}>
        <div className="modal-content" style={{ background: 'var(--bg-surface-3)', color: 'var(--text-primary)' }}>
          <div className="modal-header border-secondary">
            <h5 className="modal-title">{title}</h5>
            <button className="close text-white" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">{children}</div>
        </div>
      </div>
    </div>
  );
}

function DeployModal({ ea, onClose, onDeployed }) {
  const [symbol, setSymbol]       = useState('');
  const [timeframe, setTimeframe] = useState('H1');
  const [inputs, setInputs]       = useState([]); // [{key, value}]
  const [busy, setBusy]           = useState(false);
  const [error, setError]         = useState('');

  function setInput(i, field, val) {
    setInputs(prev => prev.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const inputMap = {};
    for (const { key, value } of inputs) {
      if (key.trim()) inputMap[key.trim()] = value;
    }
    setBusy(true);
    try {
      await api.post(`/eas/${ea._id}/deploy`, {
        symbol: symbol.trim(),
        timeframe,
        inputs: inputMap,
      });
      onDeployed();
    } catch (err) {
      setError(err.response?.data?.error || 'Deploy failed');
      setBusy(false);
    }
  }

  return (
    <Modal title={`Deploy: ${ea.name}`} onClose={busy ? () => {} : onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="alert alert-warning">
        Deploying restarts your MT5 terminal. Any other EAs you have running
        will resume automatically after the restart (~1–2 minutes).
      </div>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-7">
            <div className="form-group">
              <label>Symbol</label>
              <input
                className="form-control"
                value={symbol}
                onChange={e => setSymbol(e.target.value)}
                placeholder="e.g. EURUSD or Volatility 75 Index"
                required
              />
              <small className="form-text text-muted">
                Exactly as it appears in your broker's Market Watch.
              </small>
            </div>
          </div>
          <div className="col-md-5">
            <div className="form-group">
              <label>Timeframe</label>
              <select className="form-control" value={timeframe}
                onChange={e => setTimeframe(e.target.value)}>
                {TIMEFRAMES.map(tf => <option key={tf} value={tf}>{tf}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label className="d-flex justify-content-between align-items-center">
            <span>EA Inputs (optional)</span>
            <button type="button" className="btn btn-link btn-sm p-0"
              onClick={() => setInputs(prev => [...prev, { key: '', value: '' }])}>
              + add input
            </button>
          </label>
          {inputs.length === 0 && (
            <small className="form-text text-muted">
              Leave empty to run with the EA's built-in defaults.
            </small>
          )}
          {inputs.map((row, i) => (
            <div className="row mb-2" key={i}>
              <div className="col-5">
                <input className="form-control" placeholder="InputName"
                  value={row.key} onChange={e => setInput(i, 'key', e.target.value)} />
              </div>
              <div className="col-5">
                <input className="form-control" placeholder="value"
                  value={row.value} onChange={e => setInput(i, 'value', e.target.value)} />
              </div>
              <div className="col-2">
                <button type="button" className="btn btn-sm btn-outline-danger"
                  onClick={() => setInputs(prev => prev.filter((_, idx) => idx !== i))}>
                  &times;
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-end">
          <button type="button" className="btn btn-secondary mr-2" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Deploying…' : 'Deploy EA'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function LogsModal({ deployment, onClose }) {
  const [lines, setLines]     = useState(null);
  const [loading, setLoading] = useState(false);

  function load() {
    setLoading(true);
    api.get(`/deployments/${deployment._id}/logs`)
      .then(r => setLines(r.data.lines))
      .catch(() => setLines(['(could not load logs)']))
      .finally(() => setLoading(false));
  }

  useEffect(load, [deployment._id]);

  return (
    <Modal title={`Logs: ${deployment.eaName} (${deployment.symbol} ${deployment.timeframe})`}
      onClose={onClose} size="modal-lg">
      <div className="d-flex justify-content-end mb-2">
        <button className="btn btn-sm btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <pre style={{
        background: '#1e1e2f', color: '#d3d3d3', padding: 12,
        maxHeight: '60vh', overflow: 'auto', fontSize: '0.78rem', whiteSpace: 'pre-wrap',
      }}>
        {lines === null ? 'Loading…' : (lines.length ? lines.join('\n') : 'No log lines yet today.')}
      </pre>
    </Modal>
  );
}

export default function MyEAs() {
  const [eas, setEas]                 = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [loading, setLoading]         = useState(true);

  // upload form
  const [name, setName]               = useState('');
  const [uploading, setUploading]     = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [compileErrors, setCompileErrors] = useState('');
  const [uploadOk, setUploadOk]       = useState('');
  const fileRef = useRef(null);

  // modals
  const [deployEa, setDeployEa]       = useState(null);
  const [logsFor, setLogsFor]         = useState(null);
  const [actionError, setActionError] = useState('');
  const [busyId, setBusyId]           = useState(null);

  function refresh() {
    return Promise.all([api.get('/eas'), api.get('/deployments')])
      .then(([e, d]) => { setEas(e.data); setDeployments(d.data); });
  }

  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  async function handleUpload(e) {
    e.preventDefault();
    setUploadError(''); setCompileErrors(''); setUploadOk('');
    const file = fileRef.current?.files?.[0];
    if (!file) { setUploadError('Choose a .mq5 source file'); return; }

    const form = new FormData();
    form.append('file', file);
    form.append('name', name);
    setUploading(true);
    try {
      await api.post('/eas/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadOk(`"${name}" uploaded successfully.`);
      setName('');
      if (fileRef.current) fileRef.current.value = '';
      await refresh();
    } catch (err) {
      const data = err.response?.data || {};
      setUploadError(data.error || 'Upload failed');
      if (data.compileErrors) {
        setCompileErrors(typeof data.compileErrors === 'string'
          ? data.compileErrors
          : data.compileErrors.join('\n'));
      }
      await refresh();
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(ea) {
    if (!window.confirm(`Delete EA "${ea.name}"? This cannot be undone.`)) return;
    setActionError('');
    setBusyId(ea._id);
    try {
      await api.delete(`/eas/${ea._id}`);
      await refresh();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleStop(dep) {
    if (!window.confirm(
      `Pause "${dep.eaName}" on ${dep.symbol}? The EA is detached from its chart ` +
      `and your MT5 terminal restarts. You can resume it anytime.`)) return;
    setActionError('');
    setBusyId(dep._id);
    try {
      await api.post(`/deployments/${dep._id}/stop`);
      await refresh();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Pause failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleResume(dep) {
    if (!window.confirm(
      `Resume "${dep.eaName}" on ${dep.symbol}? This restarts your MT5 terminal.`)) return;
    setActionError('');
    setBusyId(dep._id);
    try {
      await api.post(`/deployments/${dep._id}/resume`);
      await refresh();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Resume failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeleteDeployment(dep) {
    setActionError('');
    setBusyId(dep._id);
    try {
      await api.delete(`/deployments/${dep._id}`);
      await refresh();
    } catch (err) {
      setActionError(err.response?.data?.error || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  }

  if (loading) return <p className="text-white">Loading...</p>;

  return (
    <div>
      <div className="ct-page-header">
        <div>
          <span className="eyebrow">Customer</span>
          <h1>My EAs</h1>
          <div className="subtitle">Upload, compile, deploy, and manage your Expert Advisors</div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Upload EA</h5>
            </div>
            <div className="card-body">
              {uploadError && <div className="alert alert-danger">{uploadError}</div>}
              {uploadOk && <div className="alert alert-success">{uploadOk}</div>}
              <form onSubmit={handleUpload}>
                <div className="form-group">
                  <label>EA Name</label>
                  <input className="form-control" value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="My Scalper v2" required minLength={2} maxLength={80} />
                </div>
                <div className="form-group">
                  <label>File (.mq5 source)</label>
                  <input type="file" className="form-control" ref={fileRef}
                    accept=".mq5" required />
                  <small className="form-text text-muted">
                    Upload your .mq5 source — we compile it securely on our servers and
                    compile errors right here.
                  </small>
                </div>
                <button type="submit" className="btn btn-primary" disabled={uploading}>
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
              </form>
              {compileErrors && (
                <div className="mt-3">
                  <label className="text-danger">Compile output</label>
                  <pre style={{
                    background: '#1e1e2f', color: '#ff8d72', padding: 12,
                    maxHeight: 240, overflow: 'auto', fontSize: '0.78rem', whiteSpace: 'pre-wrap',
                  }}>{compileErrors}</pre>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">My EAs ({eas.length})</h5>
            </div>
            <div className="card-body">
              {eas.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>No EAs uploaded yet.</p>
              ) : (
                <div className="table-responsive table-cards table-cards-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>Name</th><th>Type</th><th>Status</th><th>Uploaded</th><th className="text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                      {eas.map(ea => {
                        const isDeployed = deployments.some(
                          d => d.eaId === ea._id && d.status === 'deployed');
                        return (
                        <tr key={ea._id}>
                          <td data-label="Name"><strong>{ea.name}</strong></td>
                          <td data-label="Type"><code>{ea.kind === 'source' ? '.mq5' : '.ex5'}</code></td>
                          <td data-label="Status">
                            <span className={`badge ${EA_BADGE[ea.status] || 'badge-secondary'}`}>
                              {ea.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td data-label="Uploaded">{new Date(ea.createdAt).toLocaleDateString()}</td>
                          <td data-label="Actions" className="text-right">
                            {isDeployed ? (
                              <span className="badge badge-success mr-1"
                                title="This EA is running — manage it in the Deployments table below">
                                deployed
                              </span>
                            ) : (
                              <button className="btn btn-sm btn-primary mr-1"
                                disabled={ea.status !== 'ready' || busyId === ea._id}
                                onClick={() => setDeployEa(ea)}>
                                Deploy
                              </button>
                            )}
                            <button className="btn btn-sm btn-outline-danger"
                              disabled={busyId === ea._id}
                              onClick={() => handleDelete(ea)}>
                              Delete
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row mt-2">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title">Deployments</h5>
            </div>
            <div className="card-body">
              {actionError && <div className="alert alert-danger">{actionError}</div>}
              {deployments.length === 0 ? (
                <p className="text-muted" style={{ margin: 0 }}>No deployments yet — upload an EA and hit Deploy.</p>
              ) : (
                <div className="table-responsive table-cards table-cards-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>EA</th><th>Symbol</th><th>TF</th><th>Status</th><th>Since</th><th className="text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                      {deployments.map(dep => (
                        <tr key={dep._id}>
                          <td data-label="EA"><strong>{dep.eaName}</strong></td>
                          <td data-label="Symbol">{dep.symbol}</td>
                          <td data-label="TF" className="num">{dep.timeframe}</td>
                          <td data-label="Status">
                            <span className={`badge ${DEPLOYMENT_BADGE[dep.status] || 'badge-secondary'}`}>
                              {dep.status === 'stopped' ? 'paused' : dep.status}
                            </span>
                            {dep.status === 'error' && dep.lastError && (
                              <small className="d-block text-danger">{dep.lastError}</small>
                            )}
                          </td>
                          <td data-label="Since">{new Date(dep.createdAt).toLocaleString()}</td>
                          <td data-label="Actions" className="text-right">
                            <button className="btn btn-sm btn-secondary mr-1"
                              onClick={() => setLogsFor(dep)}>
                              Logs
                            </button>
                            {dep.status === 'deployed' ? (
                              <button className="btn btn-sm btn-outline-warning"
                                disabled={busyId === dep._id}
                                onClick={() => handleStop(dep)}>
                                {busyId === dep._id ? 'Pausing…' : 'Pause'}
                              </button>
                            ) : (
                              <>
                                <button className="btn btn-sm btn-outline-success mr-1"
                                  disabled={busyId === dep._id}
                                  onClick={() => handleResume(dep)}>
                                  {busyId === dep._id ? 'Resuming…' : 'Resume'}
                                </button>
                                <button className="btn btn-sm btn-outline-danger"
                                  disabled={busyId === dep._id}
                                  onClick={() => handleDeleteDeployment(dep)}>
                                  Remove
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {deployEa && (
        <DeployModal
          ea={deployEa}
          onClose={() => setDeployEa(null)}
          onDeployed={() => { setDeployEa(null); refresh(); }}
        />
      )}
      {logsFor && <LogsModal deployment={logsFor} onClose={() => setLogsFor(null)} />}
    </div>
  );
}
